import { EXTRA_LINE_ITEMS } from './types';

// Sharetribe Flex enforces a hard maximum of 64 characters on a line item
// `code`. Exceeding it makes initiate(Speculative) fail with
// `validation-invalid-value`, which blocks booking the listing entirely.
export const MAX_LINE_ITEM_CODE_LENGTH = 64;

/**
 * Truncate a line item code to Flex's 64-character limit, removing any hyphen
 * left dangling at the cut so the code stays clean.
 *
 * @param {string} code
 * @returns {string} code with length <= MAX_LINE_ITEM_CODE_LENGTH
 */
export const capLineItemCode = (code) =>
  code.length > MAX_LINE_ITEM_CODE_LENGTH
    ? code.slice(0, MAX_LINE_ITEM_CODE_LENGTH).replace(/-+$/, '')
    : code;

/**
 * Build a Flex line item `code` for a custom line item from its (already
 * english-translated) title.
 *
 * The result is always `line-item/<slug>` and is guaranteed to be at most 64
 * characters — the prefix and the optional `-custom` suffix are included in the
 * length budget (the previous implementation capped only the slug, so long
 * translated titles produced 70+ char codes that Flex rejected).
 *
 * `-custom` is appended only when the bare code would collide with a built-in
 * extra line item code (e.g. a custom item titled "Electricity" would otherwise
 * clash with `line-item/electricity`).
 *
 * @param {string} enTitle - english title of the custom line item
 * @param {string[]} [reservedCodes] - codes the result must not collide with
 * @returns {string}
 */
export const buildCustomLineItemCode = (enTitle, reservedCodes = EXTRA_LINE_ITEMS) => {
  const slug = String(enTitle || '')
    .toLowerCase()
    .replace(/ /g, '-')
    .replace(/[^a-z0-9]/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '');

  let code = `line-item/${slug}`;
  if (reservedCodes.includes(code)) {
    code = `${code}-custom`;
  }

  return capLineItemCode(code);
};
